import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from './useDebounce';

interface UseHexahqAutocompleteOptions<T> {
  searchFn: (q: string) => Promise<T[]>;
  queryKey: string;
  formatLabel: (option: T) => string;
  isOptionEqual: (a: T, b: T) => boolean;
  enabled?: boolean;
  debounceMs?: number;
  noMatchText?: string;
}

export function useHexahqAutocomplete<T>({
  searchFn,
  queryKey,
  formatLabel,
  isOptionEqual,
  enabled = true,
  debounceMs = 300,
  noMatchText = 'No results',
}: UseHexahqAutocompleteOptions<T>) {
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebounce(inputValue, debounceMs);
  const [selectedOption, setSelectedOption] = useState<T | null>(null);

  const isDisplayValue = selectedOption != null && debouncedInput === formatLabel(selectedOption);

  const { data: options = [], isFetching: isLoading } = useQuery({
    queryKey: [queryKey, debouncedInput],
    queryFn: () => searchFn(debouncedInput),
    enabled: enabled && inputValue.length >= 2 && debouncedInput.length >= 2 && !isDisplayValue,
    placeholderData: (prev) => prev,
  });

  const handleInputChange = (_e: unknown, newInput: string, reason: string) => {
    setInputValue(newInput);
    if (reason === 'clear') {
      setSelectedOption(null);
    }
  };

  return {
    inputValue,
    setInputValue,
    selectedOption,
    setSelectedOption,
    options,
    isLoading,
    formatLabel,
    isOptionEqualToValue: isOptionEqual,
    noOptionsText: inputValue.length < 2 ? 'Type to search...' : noMatchText,
    handleInputChange,
  };
}
